import { useEffect, useState } from "react";
import { listArchive } from "../api.js";
import Icon from "../ui/Icon.jsx";
import Booting from "./Booting.jsx";
import Broken from "./Broken.jsx";

const SORTS = [
  ["shaken", "가장 많이 흔들린"],
  ["steady", "가장 단단했던"],
  ["recent", "최신"],
];

/**
 * 확신도가 올라간 것도 내려간 것과 같은 자격으로 실린다. 승패가 아니다.
 * 방향은 **글자가** 말하고, 색은 밝기로만 가른다 — 색만으로 구분하면
 * 색을 못 보는 사람에게는 같은 것이 된다.
 */
function delta(n) {
  if (n < 0) return { text: `${-n} 내려감`, tint: "alert" };
  if (n > 0) return { text: `${n} 올라감`, tint: "mark" };
  return { text: "그대로", tint: "quiet" };
}

export default function Archive({ onStart }) {
  const [sort, setSort] = useState("shaken");
  const [state, setState] = useState({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    setState({ kind: "loading" });
    listArchive({ sort })
      .then((entries) => alive && setState({ kind: "ready", entries }))
      .catch((err) => alive && setState({ kind: "failed", err }));
    return () => { alive = false; };
  }, [sort]);

  if (state.kind === "failed") {
    return <Broken err={state.err} onRetry={() => setSort((s) => s)} />;
  }

  const entries = state.kind === "ready" ? state.entries : [];

  return (
    <main className="screen" style={{ minHeight: "100vh" }}>
      <header className="bar">
        <a className="logo" href="/">그래서요<span className="q">?</span></a>
        <span className="grow" />
        <button type="button" className="btn primary" style={{ minHeight: 38, fontSize: 14 }} onClick={onStart}>
          나도 해보기
        </button>
      </header>

      <h1 className="title">다른 사람들은 뭐라고 답했을까</h1>
      <p className="lead">공개에 동의한 토론만 모았어요. 원문은 올라오지 않아요.</p>

      <nav aria-label="정렬" className="scroll-x">
        {SORTS.map(([k, text]) => (
          <button key={k} type="button" aria-pressed={sort === k} onClick={() => setSort(k)}
            className="pill"
            style={sort === k
              ? { background: "var(--ink)", color: "var(--paper)", minHeight: 36 }
              : { minHeight: 36 }}>
            {text}
          </button>
        ))}
      </nav>

      {state.kind === "loading" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }} aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card" style={{ opacity: .5 }}>
              <div className="skeleton" style={{ width: "40%" }} />
              <div className="skeleton" />
            </div>
          ))}
        </div>
      ) : entries.length === 0 ? (
        <section className="card raised" style={{ borderRadius: 4, padding: "26px 22px", marginTop: 12 }}>
          <span aria-hidden="true" style={{
            alignSelf: "flex-start", width: 52, height: 52,
            border: "2px dashed var(--ink)", borderRadius: 999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="list" size={24} width={2.4} />
          </span>
          <h2 className="d" style={{ fontSize: 30, lineHeight: 1.2 }}>아직 올라온 게 없어요</h2>
          <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>
            토론을 끝내고 공개에 동의하면 여기 실려요. 첫 번째가 되어 보실래요?
          </p>
          <button type="button" className="btn primary" onClick={onStart}>주제 고르러 가기</button>
        </section>
      ) : (
        <ul className="list">
          {entries.map((e) => {
            const d = delta(e.convictionDelta ?? 0);
            return (
              <li key={e.id} className="card" style={{ gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span className="d num" style={{ fontSize: 26, lineHeight: 1, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span>{e.convictionStart}</span>
                    <Icon name="arrow" size={16} stroke="var(--muted)" width={3} />
                    <span>{e.convictionEnd}</span>
                  </span>
                  <span className={`pill${d.tint ? ` ${d.tint}` : ""}`} style={{ fontSize: 13 }}>{d.text}</span>
                  <span className="grow" />
                  <span className="note">{e.stanceLabel}</span>
                </div>
                <span className="label" style={{ color: "var(--muted)" }}>{e.topicTitle}</span>
                {e.leadConsequence?.title && (
                  <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.5 }}>{e.leadConsequence.title}</span>
                )}
                {e.pullQuote && (
                  <blockquote style={{
                    margin: 0, padding: "10px 14px", borderLeft: "4px solid var(--ink)",
                    fontSize: 14, fontWeight: 700, lineHeight: 1.55, color: "var(--muted)",
                  }}>
                    “{e.pullQuote}”
                  </blockquote>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <span className="grow" />
      <p className="note" style={{ textAlign: "center" }}>공개에 동의한 토론만 표시돼요</p>
    </main>
  );
}
